/** Context provider for the editor
 *
 * This is rather complicated, so it needs some explanation.
 * The user can load a Prompt version by visiting /projects/[promptId]/[versionId]
 * Therefore the current Prompt version id is stored in the URL, everything else is stored in this state.
 * When the user goes to /projects/[promptId]/[versionId], we load that Prompt version and store it in the context.
 * When the user selects to load a new Prompt version, we just change the URL directly.
 *
 * If the editor is in an edited state, when the page changes we warn them *unless* it's
 * a user action like an explicit project switch.
 */

import { getFileHref, pathIsProjectEditor } from "@/lib/path-utils";
import {
  INITIAL_EDITOR_PROMPT_STATE,
  getEditorPromptStateFromPrompt,
  editorPromptStateToPrompt,
  standardizeEditorPromptState,
  DEFAULT_EDITOR_PROMPT_STATE,
  editorPromptStateToAgent,
} from "@/lib/prompt-editor-utils";
import { getShortId } from "@/lib/string";
import { generateId } from "@/lib/utils";
import { File, FileType } from "@/types/app/file";
import { Prompt, PromptKernelRequest, PromptToolRequest } from "@/types/app/prompt";
import { Datum, createChatDatum, createCompletionDatum } from "@components/PromptEditor/PlaygroundData";
import { extractInputsFromEditorPromptState, extractToolsFromPrompt } from "@components/PromptEditor/utils";
import _, { isEqual } from "lodash";
import { useRouter } from "next/router";
import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChatMessage, Model, usePromptEditorModels } from "services/promptEditor.service";
import { useActiveOrganization } from "./ActiveOrganizationProvider";
import { keysToRequestInterface, useLocalProviderKeys } from "./ModelProviderKeysContext";
import { useMonitoringFile } from "./MonitoringFileContext";
import { Agent, AgentToolRequest } from "@/types/app/agent";
import { AgentKernelRequest } from "@/types/app/agent";

export type EditorMode = "chat" | "completion";

export const EDITOR_MODES: EditorMode[] = ["chat", "completion"];

type EditorVersionViewMode = "ui" | "raw";

// Additional file attribute to hold File data in state.
export type EditorTool = (PromptToolRequest | AgentToolRequest) & { file?: File };

export type EditorPromptState = Omit<PromptKernelRequest, "template" | "tools"> & {
  prompt_template?: string;
  chat_template?: ChatMessage[];
  tools?: EditorTool[];
  max_iterations?: AgentKernelRequest["max_iterations"];
};

export interface EditorState {
  mode: EditorMode;
  streaming: boolean;
  editorPromptState: EditorPromptState;
  lastSavedEditorPromptState: EditorPromptState | null;

  versionId: string | null; // Only populated if the user has loaded a version (and has not modified it).

  // Only has an effect in preview mode. Otherwise the name comes from the fetched version (by version ID and file ID).
  // See usage in EditorPromptVersionHeader
  versionPreviewNameOverride?: string;

  /** This is a way stop the "you have unsaved changes" warning
   *  when the user saves a new version.*/
  suppressUnsavedChangesWarning: boolean;
  datums: Datum[];

  versionViewMode?: EditorVersionViewMode;
}

export type EditorContextInterface = EditorState & {
  id: string;
  fileType?: FileType;
  isAgent: boolean;

  /** Replaces the Prompt, updates the prompt and the version_id so that its unedited. */
  replacePrompt: (prompt: Prompt | Agent) => void;
  /** Set the editor prompt contents - don't use this if updating the prompt version */
  setEditorPromptState: React.Dispatch<React.SetStateAction<EditorPromptState>>;

  /** Set the version ID and update the URL */
  setVersionId: (versionId: string | null) => Promise<void>;

  setLastSavedEditorPromptState: (lastSavedEditorPromptState: EditorPromptState | null) => void;
  restoreEditorPromptState: (editorPromptState: EditorPromptState) => void;
  setStreaming: (streaming: boolean) => void;
  setSuppressUnsavedChangesWarning: (suppress: boolean) => void;
  setMode: (mode: EditorMode) => void;
  setDatums: React.Dispatch<React.SetStateAction<Datum[]>>;
  hasBeenEdited: boolean;
  inputs: string[];
  tools: { name: string; call: string }[];
  hasInputs: boolean;
  models: Model[] | undefined;

  preview: boolean;
  setPreview: (partialEditorState: Partial<EditorState>) => void;
  isModifiedPreview: boolean; // Whether the preview editor state has been modified from the original editor state.

  isOnlyEditorPanel: boolean;

  setVersionViewMode: (versionViewMode: EditorVersionViewMode) => void;
};

const PromptEditorContext = createContext<EditorContextInterface | undefined>(undefined);

export const usePromptEditorContext = () => {
  const context = useContext(PromptEditorContext);
  if (context === undefined) {
    throw new Error("usePromptEditorContext must be used within a PromptEditorContextProvider");
  }
  return context;
};

/**
 * A handle to the PromptEditorContext.
 * This exposes functions to interact with the editor state.
 */
export interface PromptEditorHandle {
  setEditorPromptState: (editorPromptState: EditorPromptState) => void;
  replacePrompt: (prompt: Prompt | Agent) => void;
}

export const PromptEditorContextProvider = forwardRef<PromptEditorHandle, React.PropsWithChildren<{ id: string }>>(
  ({ id, children }, ref) => {
    const {
      getEditorState,
      setEditorState,
      setPreviewEditorStates,
      previewEditorStates,
      editorStates,
      addEditedToUrl,
      removeEditedFromUrl,
    } = useMultiPromptEditorContext();

    const editorState = getEditorState(id);
    if (editorState === undefined) {
      throw new Error(`Could not find editor state with ID ${id}. (Trying to use editor state)`);
    }
    const {
      mode,
      streaming,
      editorPromptState,
      lastSavedEditorPromptState,
      suppressUnsavedChangesWarning,
      datums,
      versionPreviewNameOverride,
      versionId,
      versionViewMode,
    } = editorState;

    const router = useRouter();
    const { slug } = useActiveOrganization();

    const { file, queryFileId } = useMonitoringFile();

    const { keys: localKeys } = useLocalProviderKeys();
    const { models } = usePromptEditorModels(keysToRequestInterface(localKeys));

    // The unprotected setStates allow us to set the state without triggering
    // the "unsaved progress" warnings shown to the user and to standardise the parameters with the defaults.
    const setModeUnprotected = useCallback(
      (newMode: EditorMode) => {
        setEditorState(id, (editorState) => ({ ...editorState, mode: newMode }));
      },
      [id, setEditorState],
    );

    const setEditorPromptStateUnprotected = useCallback(
      (
        newEditorPromptState: EditorPromptState | ((currentEditorPromptState: EditorPromptState) => EditorPromptState),
      ) => {
        setEditorState(id, (editorState) => ({
          ...editorState,
          editorPromptState:
            typeof newEditorPromptState === "function"
              ? newEditorPromptState(editorState.editorPromptState)
              : newEditorPromptState,
        }));
      },
      [id, setEditorState],
    );

    const setStreaming = useCallback(
      (streaming: boolean) => {
        setEditorState(id, (editorState) => ({ ...editorState, streaming }));
      },
      [id, setEditorState],
    );

    const modelsRef = useRef(models); // Avoid mutating `standardise`.
    useEffect(() => {
      modelsRef.current = models;
    }, [models]);

    const standardise = useCallback((editorPromptState: EditorPromptState): EditorPromptState => {
      const modelDetails = modelsRef.current?.find((model) => model.name === editorPromptState.model);
      return standardizeEditorPromptState(editorPromptState, modelDetails);
    }, []);

    const setLastSavedEditorPromptState = useCallback(
      (newEditorPromptState: EditorPromptState | null) => {
        setEditorState(id, (editorState) => ({
          ...editorState,
          lastSavedEditorPromptState: newEditorPromptState ? standardise(newEditorPromptState) : null,
        }));
      },
      [id, setEditorState, standardise],
    );

    const preview = previewEditorStates !== null; // Whether the editor is in preview mode. (see `isModifiedPreview` below)

    // Used to show a preview of the editor state on hover in the history list etc.
    const setPreview = useCallback(
      (partialEditorState: Partial<EditorState>) => {
        setPreviewEditorStates((previewEditorStates) => {
          // Process partial state being set as preview
          // (currently only involves standardising the editor prompt state)
          const processedPartialEditorState = {
            ...partialEditorState,
          };
          if (processedPartialEditorState.editorPromptState) {
            processedPartialEditorState.editorPromptState = standardise(processedPartialEditorState.editorPromptState);

            processedPartialEditorState.versionPreviewNameOverride =
              "id" in processedPartialEditorState.editorPromptState
                ? getShortId(processedPartialEditorState.editorPromptState.id as string)
                : "Previewing";
          }

          // If not in preview mode, enter preview mode using the current editor states.
          const sourcePreviewEditorState = previewEditorStates ?? editorStates;

          return sourcePreviewEditorState.map((editorState) => {
            if (editorState.id === id) {
              return {
                id,
                state: {
                  ...editorState.state,
                  ...processedPartialEditorState,
                },
              };
            }
            return editorState;
          });
        });
      },
      [id, editorStates, setPreviewEditorStates, standardise],
    );

    // Whether the preview editor state has been modified from the original editor state.
    // Returns false if the preview editor state does not correspond to any non-preview state.
    const isModifiedPreview = useMemo(() => {
      if (previewEditorStates === null) {
        return false;
      }
      const nonPreviewEditorState = editorStates.find((editorState) => editorState.id === id)?.state;
      const previewEditorState = editorState;
      if (nonPreviewEditorState === undefined) {
        return false;
      }
      return nonPreviewEditorState !== previewEditorState;
    }, [id, editorState, editorStates, previewEditorStates]);

    const hasBeenEdited = useMemo(() => {
      if (file?.type === "agent") {
        const current = editorPromptStateToAgent({
          editorPromptState: standardise(editorPromptState),
          mode: lastSavedEditorPromptState?.endpoint === "complete" ? "completion" : "chat",
        });
        const saved = lastSavedEditorPromptState
          ? editorPromptStateToAgent({
              editorPromptState: standardise(lastSavedEditorPromptState),
              mode: lastSavedEditorPromptState?.endpoint === "complete" ? "completion" : "chat",
            })
          : null;
        return !isEqual(current, saved);
      }

      const current = editorPromptStateToPrompt({
        editorPromptState: standardise(editorPromptState),
        mode: lastSavedEditorPromptState?.endpoint === "complete" ? "completion" : "chat",
      });
      const saved = lastSavedEditorPromptState
        ? editorPromptStateToPrompt({
            editorPromptState: standardise(lastSavedEditorPromptState),
            mode: lastSavedEditorPromptState?.endpoint === "complete" ? "completion" : "chat",
          })
        : null;
      return !isEqual(current, saved);
    }, [file?.type, standardise, editorPromptState, lastSavedEditorPromptState]);

    useEffect(() => {
      if (hasBeenEdited) {
        console.log("Detected hasBeenEdited; adding edited to URL.");
        addEditedToUrl.current?.();
      }
    }, [addEditedToUrl, hasBeenEdited]);

    const setSuppressUnsavedChangesWarning = useCallback(
      (suppress: boolean) => {
        setEditorState(id, (editorState) => ({ ...editorState, suppressUnsavedChangesWarning: suppress }));
      },
      [id, setEditorState],
    );

    const isOnlyEditorPanel =
      previewEditorStates === null ? editorStates.length === 1 : previewEditorStates.length === 1; // Whether this is the only editor panel.

    /** Set the version ID in the editor state and update the URL if only editor (i.e. single editor panel) - doesn't directly load the version */
    const setVersionId = useCallback(
      async (
        versionId: string | null,
        dataIds: string[] | undefined = undefined, // If these are specified, they will be added to the URL as query params (if we decide to set query params)
      ) => {
        console.log("Setting version ID", { versionId, dataIds });
        if (!pathIsProjectEditor(router.asPath)) {
          console.log("Not setting version ID in URL as we are not in Prompt/Agent editor");
          return;
        }
        setEditorState(id, (editorState) => ({ ...editorState, versionId }));

        // Set URL if this is the only editor panel.
        if ((versionId || dataIds) && isOnlyEditorPanel) {
          const url = getEditorUrl({
            slug: slug ?? router.query.slug ?? "",
            projectId: queryFileId ?? (router.query.projectId as string | undefined) ?? "",
            // Keep the dataIds in the URL if they are already there.
            dataIds:
              dataIds || router.query.dataIds
                ? _.isArray(router.query.dataIds)
                  ? (router.query.dataIds as string[])
                  : [router.query.dataIds as string]
                : undefined,
            versionId,
          });
          console.log("Pushing URL", url);
          // Shallow to not refetch anything.
          if (router.isReady && router.asPath !== url) {
            await router.push(url, undefined, { shallow: true });
          }
          removeEditedFromUrl.current?.();
        }
      },
      [id, isOnlyEditorPanel, queryFileId, removeEditedFromUrl, router, setEditorState, slug],
    );

    /** Given a new prompt, replace the editor state and version ID. */
    const replacePrompt = useCallback(
      (prompt: Prompt | Agent) => {
        setEditorPromptStateUnprotected(standardise(getEditorPromptStateFromPrompt(prompt)));
        setVersionId(prompt.version_id);
        setLastSavedEditorPromptState(getEditorPromptStateFromPrompt(prompt));
      },
      [setEditorPromptStateUnprotected, standardise, setVersionId, setLastSavedEditorPromptState],
    );

    /** Set the model config contents, with the side effects */
    const setEditorPromptState: React.Dispatch<React.SetStateAction<EditorPromptState>> = useCallback(
      (newEditorPromptState: EditorPromptState | ((currentState: EditorPromptState) => EditorPromptState)) => {
        setEditorPromptStateUnprotected((currentState: EditorPromptState) => {
          let newState =
            typeof newEditorPromptState === "function" ? newEditorPromptState(currentState) : newEditorPromptState;
          const modelSupportsReasoning = models?.find(
            (model) => model.name === newState.model,
          )?.reasoning_effort_support;

          const modelSupportsJsonSchemaResponse =
            models?.find(
              (model) => model.name === newState.model,
              // If model not found in the list, it's likely a custom model (e.g. from Azure).
              // Default to true to allow JSON schema functionality.
              // Worst case Azure will throw an error and we can handle it gracefully.
            )?.json_schema_response_support ?? true;

          if (!modelSupportsReasoning) {
            newState = {
              ...newState,
              reasoning_effort: undefined,
            };
          }
          if (
            modelSupportsReasoning &&
            (currentState.provider === "openai" || currentState.provider === "openai_azure") &&
            newState.reasoning_effort === undefined
          ) {
            // Open AI defaults to "medium" but we want to set it up explicitly here.
            newState = {
              ...newState,
              reasoning_effort: DEFAULT_EDITOR_PROMPT_STATE.reasoning_effort,
            };
          }

          if (modelSupportsReasoning && currentState.provider !== newState.provider) {
            // Switching from openai to anthropic
            newState = {
              ...newState,
              // Let standardize handle the undefined
              reasoning_effort: undefined,
            };
          }

          if (
            modelSupportsReasoning &&
            newState.provider === "anthropic" &&
            typeof newState.reasoning_effort === "number"
          ) {
            // Turn off temperature, top_p, and top_k for Anthropic reasoning models
            newState = {
              ...newState,
              temperature: undefined,
              top_p: undefined,
            };
          }
          // If modelSupportsJsonSchemaResponse is undefined, it means we don't have the model
          // in our registry and it's likely a custom model (e.g. from Azure).
          // In this case, assume json_schema is supported.
          // Worst case Azure will throw an error and we can handle it gracefully.
          if (modelSupportsJsonSchemaResponse === false && newState.response_format?.type === "json_schema") {
            newState = {
              ...newState,
              response_format: undefined,
            };
          }
          return standardise(newState);
        });
      },
      [setEditorPromptStateUnprotected, models, standardise],
    );

    // Prompt manipulation
    const prompt = editorPromptState.prompt_template;
    const inputs = useMemo(() => extractInputsFromEditorPromptState(editorPromptState), [editorPromptState]);
    const hasInputs = inputs.length > 0;
    const tools = useMemo(() => (prompt ? extractToolsFromPrompt(prompt) : []), [prompt]);
    const defaultTemplate = models?.find((model) => model.name === editorPromptState.model)?.default_prompt_template;

    useEffect(() => {
      if (editorPromptState.prompt_template === "" && defaultTemplate != undefined) {
        setEditorPromptStateUnprotected((editorPromptState) => ({
          ...editorPromptState,
          prompt_template: defaultTemplate,
        }));
      }
      // Want to allow the user to delete the prompt so not including the prompt in the dep array
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorPromptState.model, prompt]);

    const setDatums = useCallback(
      (newDatums: Datum[] | ((currentDatums: Datum[]) => Datum[])) => {
        setEditorState(id, (editorState) => ({
          ...editorState,
          datums: typeof newDatums === "function" ? newDatums(editorState.datums) : newDatums,
        }));
      },
      [id, setEditorState],
    );

    const setMode = useCallback(
      async (newMode: EditorMode) => {
        if (newMode === "chat") {
          // If datums does not contain any chat datums, add one in.
          if (!datums.some((datum) => datum.type === "chat")) {
            setDatums((datums) => [createChatDatum(), ...datums]);
          }
          if (editorPromptState.endpoint !== "chat") {
            setEditorPromptStateUnprotected((editorPromptState) => ({ ...editorPromptState, endpoint: "chat" }));
          }
        } else if (newMode === "completion") {
          // If datums does not contain any completion datums, add some in
          if (!datums.some((datum) => datum.type === "completion")) {
            setDatums((datums) => [createCompletionDatum(), createCompletionDatum(), ...datums]);
          }
          // Raise error if there are existing datums that are not completion datums
          if (editorPromptState.endpoint !== "complete") {
            setEditorPromptStateUnprotected((editorPromptState) => ({ ...editorPromptState, endpoint: "complete" }));
          }
        }
        setModeUnprotected(newMode);
        console.log("Setting mode to", newMode);
      },
      [datums, editorPromptState.endpoint, setDatums, setModeUnprotected, setEditorPromptStateUnprotected],
    );

    // Set mode based on Prompt.
    // TODO: Consider improving this behaviour. This is triggered when the version dropdown is used.
    useEffect(() => {
      if (editorPromptState.endpoint === "chat" && mode !== "chat") {
        setModeUnprotected("chat");
        console.log("Setting mode to chat because prompt's endpoint is chat");
      } else if (editorPromptState.endpoint === "complete" && mode !== "completion") {
        setModeUnprotected("completion");
        console.log("Setting mode to completion because prompt's endpoint is complete");
      }
    }, [editorPromptState.endpoint, mode, setModeUnprotected]);

    const restoreEditorPromptState = useCallback(
      (editorPromptState: EditorPromptState) => {
        setEditorPromptState(editorPromptState);
        setLastSavedEditorPromptState(editorPromptState);
      },
      [setLastSavedEditorPromptState, setEditorPromptState],
    );

    const setVersionViewMode = useCallback(
      (versionViewMode: EditorVersionViewMode) => {
        setEditorState(id, (editorState) => ({ ...editorState, versionViewMode }));
      },
      [id, setEditorState],
    );

    useImperativeHandle(
      ref,
      () => ({
        setEditorPromptState,
        replacePrompt,
      }),
      [setEditorPromptState, replacePrompt],
    );

    return (
      <PromptEditorContext.Provider
        value={{
          id,
          fileType: file?.type,
          isAgent: file?.type === "agent",
          inputs,
          tools,
          hasInputs,
          editorPromptState,
          replacePrompt,
          setEditorPromptState,

          versionId,
          setVersionId,

          lastSavedEditorPromptState,
          setLastSavedEditorPromptState,
          restoreEditorPromptState,
          hasBeenEdited,

          models,
          streaming,
          setStreaming,
          suppressUnsavedChangesWarning,
          setSuppressUnsavedChangesWarning,

          mode,
          setMode,

          datums,
          setDatums,

          preview,
          setPreview,
          isModifiedPreview,

          versionPreviewNameOverride,
          isOnlyEditorPanel,

          versionViewMode,
          setVersionViewMode,
        }}
      >
        {children}
      </PromptEditorContext.Provider>
    );
  },
);

PromptEditorContextProvider.displayName = "PromptEditorContextProvider";

export interface EditorStateWithId {
  id: string;
  state: EditorState;
}

interface AddEditorStateOptions {
  // If sourceId is specified, the new editor state will be a copy of the source editor state.
  // Otherwise, the new editor state will be a blank (default) editor state.
  sourceId?: string;
  // If addAfterId is specified, the new editor state will be added after the editor state with the specified ID.
  // Otherwise, the new editor state will be added at the end.
  addAfterId?: string;
}

export interface MultiPromptEditor {
  editorStates: EditorStateWithId[];
  setEditorStates: React.Dispatch<React.SetStateAction<EditorStateWithId[]>>;
  getEditorState: (id: string) => EditorState | undefined;
  setEditorState: (id: string, newEditorState: React.SetStateAction<EditorState>) => void;
  addEditorState: (options: AddEditorStateOptions) => void;
  removeEditorState: (id: string) => void;

  previewEditorStates: EditorStateWithId[] | null;
  setPreviewEditorStates: React.Dispatch<React.SetStateAction<EditorStateWithId[] | null>>;

  // Used to suppress loading model config and datums from URL.
  // (E.g. when a user has modified the editor state and refreshes the page)
  editedInUrl: React.RefObject<boolean>;
  addEditedToUrl: React.RefObject<() => void>;
  removeEditedFromUrl: React.RefObject<() => void>;

  // Imperative handles to the editor states.
  editorRefs: React.RefObject<{ [id: string]: React.RefObject<PromptEditorHandle> }>;
}

const MultiPromptEditorContext = createContext<MultiPromptEditor | undefined>(undefined);

export const useMultiPromptEditorContext = () => {
  const context = useContext(MultiPromptEditorContext);
  if (context === undefined) {
    throw new Error("useMultiPromptEditorContext must be used within a MultiPromptEditorContextProvider");
  }
  return context;
};

const MultiPromptEditorContextProviderComponent = ({ children }: React.PropsWithChildren<{}>) => {
  const router = useRouter();

  // Add a ref to store all editor refs
  const editorRefs = useRef<{ [id: string]: React.RefObject<PromptEditorHandle> }>({});

  // Initialize refs for initial editor state
  useEffect(() => {
    editorStates.forEach((state) => {
      if (!editorRefs.current[state.id]) {
        editorRefs.current[state.id] = React.createRef<PromptEditorHandle>();
      }
    });
  }, []); // Only run once on mount

  const editedInUrl = useRef(false);
  useEffect(() => {
    if (router.query.edited) {
      console.log("Detected edited in URL; suppressing loading from URL");
      editedInUrl.current = true;
    } else {
      console.log("Detected edited no longer in URL; will load from URL");
      editedInUrl.current = false;
    }
  }, [router.query.edited]);

  // Refs to avoid these helpers triggering rerenders.
  const addEditedToUrl = useRef<(() => void) | null>(null);
  const removeEditedFromUrl = useRef<(() => void) | null>(null);
  addEditedToUrl.current = () => {
    console.log(`Adding edited to URL`, router.query.edited);
    if (router.query.edited === undefined && router.isReady) {
      router.replace(
        {
          pathname: router.pathname,
          query: { ...router.query, edited: "true" },
        },
        undefined,
        { shallow: true },
      );
    }
    // While this is normally inferred from the URL, this is set here immediately so it takes effect even before the next rerenders.
    editedInUrl.current = true;
  };
  removeEditedFromUrl.current = () => {
    console.log(`Removing edited from URL`, router.query.edited);
    if (router.query.edited !== undefined && router.isReady) {
      delete router.query.edited;
      router.replace(
        {
          pathname: router.pathname,
          query: _.omit(router.query, "edited"),
        },
        undefined,
        { shallow: true },
      );
    }
    // While this is normally inferred from the URL, this is set here immediately so it takes effect even before the next rerenders.
    editedInUrl.current = false;
  };

  const [editorStates, _setEditorStates] = useState<EditorStateWithId[]>(() => [
    { id: generateEditorId(), state: createDefaultEditorState() },
  ]);

  // Wrap setEditorStates to manage refs when states change directly
  const setEditorStates = useCallback(
    (newEditorStates: EditorStateWithId[] | ((prevStates: EditorStateWithId[]) => EditorStateWithId[])) => {
      _setEditorStates((prevStates) => {
        const nextStates = typeof newEditorStates === "function" ? newEditorStates(prevStates) : newEditorStates;

        // Clean up refs for removed states
        Object.keys(editorRefs.current).forEach((id) => {
          if (!nextStates.some((state) => state.id === id)) {
            delete editorRefs.current[id];
          }
        });

        // Add refs for new states
        nextStates.forEach((state) => {
          if (!editorRefs.current[state.id]) {
            editorRefs.current[state.id] = React.createRef<PromptEditorHandle>();
          }
        });

        return nextStates;
      });
    },
    [],
  );

  const [previewEditorStates, setPreviewEditorStates] = useState<EditorStateWithId[] | null>(null);

  const getEditorState = (id: string) => {
    if (previewEditorStates !== null) {
      return previewEditorStates.find((editorState) => editorState.id === id)?.state;
    }
    return editorStates.find((editorState) => editorState.id === id)?.state;
  };

  const setEditorState = useCallback(
    (id: string, newEditorState: React.SetStateAction<EditorState>) => {
      setEditorStates((editorStates) => {
        const editorState = editorStates.find((editorState) => editorState.id === id);
        if (editorState === undefined) {
          console.error(`Could not find editor state with ID ${id}. (Trying to set editor state)`);
          return editorStates;
        }

        return editorStates.map((editorState) => {
          if (editorState.id === id) {
            if (typeof newEditorState === "function") {
              const updatedEditorState = newEditorState(editorState.state);
              return { id, state: updatedEditorState };
            } else {
              return { id, state: newEditorState };
            }
          }
          return editorState;
        });
      });
    },
    [setEditorStates],
  );

  const addEditorState = useCallback(
    ({ sourceId, addAfterId }: AddEditorStateOptions) => {
      setEditorStates((editorStates) => {
        // Create a new editor state from the source editor state (if specified)
        // or a default editor state.
        let state: EditorState;
        if (sourceId !== undefined) {
          // Copy the state from the source editor
          const sourceState = editorStates.find((editorState) => editorState.id === sourceId)?.state;
          if (sourceState === undefined) {
            throw new Error(
              `Could not find editor state with ID ${sourceId}. (Trying to add editor state from source)`,
            );
          }
          state = _.cloneDeep(sourceState);
        } else {
          // Create a default editor state
          state = createDefaultEditorState();
        }

        const newEditorState: EditorStateWithId = {
          id: generateEditorId(),
          state,
        };

        // Add the new editor state to the editor states array
        // in the correct position.
        if (addAfterId !== undefined) {
          const addAfterIndex = editorStates.findIndex((editorState) => editorState.id === addAfterId);
          if (addAfterIndex === -1) {
            throw new Error(
              `Could not find editor state with ID ${addAfterId}. (Trying to add editor state after specified ID)`,
            );
          }
          return [
            ...editorStates.slice(0, addAfterIndex + 1),
            newEditorState,
            ...editorStates.slice(addAfterIndex + 1),
          ];
        } else {
          return [...editorStates, newEditorState];
        }
      });
      console.log("Adding edited to URL while adding editor state.");
      addEditedToUrl.current?.();
    },
    [setEditorStates],
  );

  const removeEditorState = useCallback(
    (id: string) => {
      setEditorStates((editorStates) => editorStates.filter((editorState) => editorState.id !== id));
    },
    [setEditorStates],
  );

  return (
    <MultiPromptEditorContext.Provider
      value={{
        editorStates,
        setEditorStates,
        getEditorState,
        setEditorState,
        addEditorState,
        removeEditorState,

        previewEditorStates,
        setPreviewEditorStates,

        editedInUrl,
        addEditedToUrl,
        removeEditedFromUrl,

        editorRefs,
      }}
    >
      {children}
    </MultiPromptEditorContext.Provider>
  );
};

export const MultiPromptEditorContextProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const { queryFileId } = useMonitoringFile({ allowUndefined: true }) ?? { queryFileId: null };
  return (
    // key by `queryFileId` so that the context is recreated when the file changes.
    // We were otherwise seeing the generated assistant messages persist across file changes.
    <MultiPromptEditorContextProviderComponent key={queryFileId}>{children}</MultiPromptEditorContextProviderComponent>
  );
};

export const generateEditorId = () => generateId();

// TODO: Reorder the types in this file. Its current order is a result of development and makes no sense.

export const createDefaultEditorState = (): EditorState => {
  return {
    mode: "chat",
    streaming: true,
    editorPromptState: standardizeEditorPromptState(INITIAL_EDITOR_PROMPT_STATE),
    lastSavedEditorPromptState: standardizeEditorPromptState(INITIAL_EDITOR_PROMPT_STATE),
    versionId: null,
    suppressUnsavedChangesWarning: false,
    datums: [createCompletionDatum(), createCompletionDatum(), createCompletionDatum(), createChatDatum()],
  };
};

const MAXIMUM_PANELS_COUNT = 10;

export const useNewPanelAllowed = (): boolean => {
  const { editorStates } = useMultiPromptEditorContext();
  const currentPanelCount = editorStates.length;
  return currentPanelCount < MAXIMUM_PANELS_COUNT;
};

/**
 * Get the URL to be navigated to for the given projectId and versionId and dataIds.
 */
const getEditorUrl = ({
  slug,
  projectId,
  versionId,
  dataIds,
}: {
  slug: string;
  projectId: string;
  versionId?: string | null;
  dataIds?: string[];
}): string => {
  // The router asPath will start with  /project/pr_.../editor.
  // This will change config id in the paths after that which can include config_... and query params.
  const basePath = getFileHref({ slug, id: projectId, page: "editor" });
  let url = versionId ? `${basePath}/${versionId}` : basePath;
  if (dataIds) {
    const params = new URLSearchParams();
    dataIds.forEach((id) => params.append("dataIds", id));
    url += `?${params.toString()}`;
  }
  return url;
};
